import React, { useState } from "react";
import styled from "@emotion/styled";
import Text from "@components/Text";
import Tabs from "@components/Tabs";

const Container = styled.div`
  max-width: 1000px;
  padding-bottom: 48px;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionHeader = styled.div<{ open?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 12px 0;
  border-bottom: 1px solid #ebeeef;
  user-select: none;
  &:hover {
    background: #fafafa;
  }
`;

const Arrow = styled.span<{ open?: boolean }>`
  font-size: 12px;
  color: #899093;
  transition: transform 0.2s;
  transform: ${({ open }) => (open ? "rotate(90deg)" : "rotate(0)")};
`;

const EndpointList = styled.div`
  padding: 8px 0;
`;

const Endpoint = styled.div<{ expanded?: boolean }>`
  border: 1px solid #ebeeef;
  border-radius: 6px;
  margin-bottom: 8px;
  overflow: hidden;
`;

const EndpointHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  cursor: pointer;
  &:hover {
    background: #fafafa;
  }
`;

const MethodBadge = styled.span<{ method: string }>`
  font-family: monospace;
  font-size: 12px;
  font-weight: bold;
  padding: 2px 8px;
  border-radius: 4px;
  min-width: 40px;
  text-align: center;
  color: white;
  background: ${({ method }) => {
    switch (method) {
      case "GET":
        return "#4a90d9";
      case "POST":
        return "#68ad18";
      case "PUT":
        return "#f0ad4e";
      case "PATCH":
        return "#f0ad4e";
      case "DELETE":
        return "#e74c3c";
      default:
        return "#899093";
    }
  }};
`;

const EndpointPath = styled.code`
  font-family: monospace;
  font-size: 13px;
  color: #494c5b;
`;

const EndpointDesc = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #899093;
  margin-left: auto;
`;

const EndpointDetail = styled.div`
  padding: 16px;
  background: #f8f9fa;
  border-top: 1px solid #ebeeef;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #494c5b;
`;

const ParamsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
  margin-bottom: 16px;

  th,
  td {
    text-align: left;
    padding: 6px 12px;
    border-bottom: 1px solid #ebeeef;
    font-size: 12px;
  }
  th {
    color: #899093;
    font-weight: 600;
  }
  td code {
    font-family: monospace;
    background: #e8e8e8;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 11px;
  }
`;

const ResponseExample = styled.pre`
  background: #2d2d2d;
  color: #f8f8f2;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 12px;
  overflow-x: auto;
  line-height: 1.5;
  margin-top: 8px;
`;

const Label = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: #899093;
  margin-top: 12px;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Note = styled.div`
  background: #fff8e1;
  border-left: 3px solid #f0ad4e;
  padding: 8px 12px;
  font-size: 12px;
  color: #6d5a00;
  margin-bottom: 16px;
  border-radius: 0 4px 4px 0;
`;

type Param = {
  name: string;
  type: string;
  required: boolean;
  description: string;
};

type EndpointDef = {
  method: string;
  path: string;
  description: string;
  params?: Param[];
  bodyParams?: Param[];
  response?: string;
};

type SectionDef = {
  title: string;
  description: string;
  endpoints: EndpointDef[];
};

const API_SECTIONS: SectionDef[] = [
  {
    title: "Authentication",
    description: "All API endpoints require authentication via session cookies (Better Auth). Admin endpoints additionally check that the user's email is in the ADMIN_EMAILS list.",
    endpoints: [
      {
        method: "POST",
        path: "/api/auth/sign-in/email",
        description: "Sign in with email/password",
        bodyParams: [
          { name: "email", type: "string", required: true, description: "User email" },
          { name: "password", type: "string", required: true, description: "User password" },
        ],
        response: `{ "user": { "id": "...", "email": "...", "name": "..." }, "session": { "token": "..." } }`,
      },
    ],
  },
  {
    title: "Admin - Users",
    description: "User management endpoints. Requires admin access.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/me",
        description: "Check admin status",
        response: `{ "isAdmin": true, "email": "admin@example.com" }`,
      },
      {
        method: "GET",
        path: "/api/admin/users",
        description: "List all users with device count",
        params: [
          { name: "search", type: "string", required: false, description: "Search by name, email, or organization" },
          { name: "limit", type: "number", required: false, description: "Page size (default 50)" },
          { name: "offset", type: "number", required: false, description: "Pagination offset (default 0)" },
        ],
        response: `{ "users": [{ "id": "...", "name": "...", "email": "...", "phoneNumber": "...", "organizationName": "...", "createdAt": "...", "deviceCount": 2 }], "total": 337 }`,
      },
      {
        method: "GET",
        path: "/api/admin/users/:id",
        description: "Get user detail with devices, field/season counts",
        response: `{ "user": { ... }, "devices": [{ "esn": "...", "name": "..." }], "fieldsCount": 5, "seasonsCount": 2 }`,
      },
      {
        method: "GET",
        path: "/api/admin/users/:id/settings",
        description: "Get user settings",
        response: `{ "userId": "...", "settings": { ... }, "schemaVersion": "1.0.0" }`,
      },
      {
        method: "PUT",
        path: "/api/admin/users/:id/settings",
        description: "Update user settings",
        bodyParams: [
          { name: "settings", type: "object", required: true, description: "Settings JSON object" },
          { name: "schemaVersion", type: "string", required: false, description: "Schema version reference" },
        ],
        response: `{ "ok": true }`,
      },
    ],
  },
  {
    title: "Admin - Devices",
    description: "Device management with full hardware details. Requires admin access.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/devices",
        description: "List all devices with hardware info and attached user",
        params: [
          { name: "search", type: "string", required: false, description: "Search by ESN, alias, IMEI, ICCID, or model" },
          { name: "limit", type: "number", required: false, description: "Page size (default 50)" },
          { name: "offset", type: "number", required: false, description: "Pagination offset" },
        ],
        response: `{ "devices": [{ "esn": "...", "imei": "...", "iccid": "...", "bootloaderVersion": "...", "attachedUser": { "id": "...", "name": "..." } }], "total": 120 }`,
      },
      {
        method: "GET",
        path: "/api/admin/devices/:esn",
        description: "Full device detail with attachment history",
        response: `{ "device": { ... }, "status": { "lastConnectedAt": "...", "lastLongitude": 37.6, "lastLatitude": 55.7 }, "attachments": [...] }`,
      },
    ],
  },
  {
    title: "Admin - Tracks (Analytics)",
    description: "Track data endpoints for analytics scripts. Access tracks by device ESN without user-level ownership checks.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/tracks",
        description: "Get track points by device ESN. Filter by device time (from+to) or by ingestion time (sinceId, or createdFrom/createdTo) — one of the two is required.",
        params: [
          { name: "esn", type: "string", required: true, description: "Device ESN" },
          { name: "from", type: "datetime", required: false, description: "Device-time start (ISO 8601). Must be paired with `to`." },
          { name: "to", type: "datetime", required: false, description: "Device-time end (ISO 8601). Must be paired with `from`." },
          { name: "sinceId", type: "number", required: false, description: "Ingestion cursor: only rows with id greater than this, ordered by id. Pass back the previous response's `maxId`." },
          { name: "createdFrom", type: "datetime", required: false, description: "Ingestion-time start (ISO 8601) — when the row was written here" },
          { name: "createdTo", type: "datetime", required: false, description: "Ingestion-time end (ISO 8601)" },
          { name: "limit", type: "number", required: false, description: "Max points (default 100000)" },
          { name: "sample", type: "number", required: false, description: "Return every Nth point (e.g. 5 = every 5th)" },
          { name: "includeTotal", type: "boolean", required: false, description: "Add `total` — rows matching the filter ignoring limit. Pair with limit=1 to reconcile counts without downloading rows." },
        ],
        response: `{ "esn": "GG-001", "length": 5432, "total": 5432, "maxId": 17190000, "hasMore": false, "orderedBy": "id", "tracks": [{ "id": 1, "time": "...", "createdAt": "...", "longitude": 37.6, "latitude": 55.7, "value": 120.5, "freq": 850, "speed": 5.2, ... }] }`,
      },
      {
        method: "GET",
        path: "/api/admin/tracks/csv",
        description: "Export tracks as CSV file",
        params: [
          { name: "esn", type: "string", required: true, description: "Device ESN" },
          { name: "from", type: "datetime", required: true, description: "Start time (ISO 8601)" },
          { name: "to", type: "datetime", required: true, description: "End time (ISO 8601)" },
          { name: "sample", type: "number", required: false, description: "Return every Nth point" },
        ],
        response: `id,time,longitude,latitude,value,freq,course,...\n1,2025-07-15T10:00:00Z,37.6,55.7,120.5,850,180,...`,
      },
      {
        method: "GET",
        path: "/api/admin/tracks/geometry",
        description: "Get track as GeoJSON FeatureCollection with values, speeds, and times",
        params: [
          { name: "esn", type: "string", required: true, description: "Device ESN" },
          { name: "from", type: "datetime", required: true, description: "Start time (ISO 8601)" },
          { name: "to", type: "datetime", required: true, description: "End time (ISO 8601)" },
          { name: "sample", type: "number", required: false, description: "Return every Nth point" },
        ],
        response: `{ "type": "FeatureCollection", "features": [{ "type": "Feature", "geometry": { "type": "LineString", "coordinates": [[37.6, 55.7], ...] }, "properties": { "esn": "GG-001", "pointCount": 5432, "values": [...], "speeds": [...] } }] }`,
      },
    ],
  },
  {
    title: "Admin - Fields (Analytics)",
    description: "Global field access for analytics. Not scoped to a single user.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/fields",
        description: "List all fields across all users",
        params: [
          { name: "userId", type: "string", required: false, description: "Filter by user ID" },
          { name: "search", type: "string", required: false, description: "Search by field name" },
          { name: "includeDeleted", type: "boolean", required: false, description: "Include soft-deleted fields (default false)" },
          { name: "limit", type: "number", required: false, description: "Page size (default 100)" },
          { name: "offset", type: "number", required: false, description: "Pagination offset" },
        ],
        response: `{ "fields": [{ "id": "...", "userId": "...", "userName": "...", "name": "North Field", "geometry": { ... }, "areaHectares": 42.5 }], "total": 200 }`,
      },
      {
        method: "GET",
        path: "/api/admin/fields/:id",
        description: "Get field detail with cultures and owner info",
        response: `{ "field": { "id": "...", "userName": "...", "name": "...", "geometry": { ... }, "areaHectares": 42.5 }, "cultures": [{ "cultureName": "Wheat", "variety": "Winter" }] }`,
      },
    ],
  },
  {
    title: "Admin - Calibrations (Analytics)",
    description: "Calibration data for yield coefficient computation scripts.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/calibrations",
        description: "List all calibrations with filtering",
        params: [
          { name: "esn", type: "string", required: false, description: "Filter by device ESN" },
          { name: "fieldId", type: "string", required: false, description: "Filter by field ID" },
          { name: "from", type: "datetime", required: false, description: "Filter by start time" },
          { name: "to", type: "datetime", required: false, description: "Filter by stop time" },
          { name: "needsCoefficient", type: "boolean", required: false, description: "Only calibrations without coefficient" },
          { name: "limit", type: "number", required: false, description: "Page size (default 100)" },
          { name: "offset", type: "number", required: false, description: "Pagination offset" },
        ],
        response: `{ "calibrations": [{ "id": "...", "attachmentId": "...", "fieldId": "...", "start": "...", "stop": "...", "weightKilograms": 5000, "coefficient": 0.85, "coefficientUpdatedAt": "..." }], "total": 50 }`,
      },
      {
        method: "PUT",
        path: "/api/admin/calibrations/:id/coefficient",
        description: "Update calibration coefficient (from analytics script)",
        bodyParams: [
          { name: "coefficient", type: "number", required: true, description: "Computed yield coefficient" },
        ],
        response: `{ "ok": true }`,
      },
      {
        method: "GET",
        path: "/api/admin/calibrations/updates",
        description: "Cursor-based change history. Returns calibrations created/updated/deleted since cursor revision. Includes soft-deleted records.",
        params: [
          { name: "cursor", type: "number", required: false, description: "Revision number to start from (default 0)" },
          { name: "esn", type: "string", required: false, description: "Filter by device ESN" },
          { name: "fieldId", type: "string", required: false, description: "Filter by field ID" },
          { name: "limit", type: "number", required: false, description: "Max records to return (default 100, max 1000)" },
        ],
        response: `{ "calibrations": [{ "id": "...", "revision": 42, "deletedAt": null, ... }], "cursor": 42 }`,
      },
    ],
  },
  {
    title: "Internal API - Calibrations",
    description: "Service-to-service endpoints (no user auth, protect via network). Used by external coefficient calculation scripts.",
    endpoints: [
      {
        method: "GET",
        path: "/api/internal/attachments/:attachmentId/calibrations",
        description: "Get active calibrations for a device by time interval",
        params: [
          { name: "start", type: "datetime", required: false, description: "Filter by start time" },
          { name: "end", type: "datetime", required: false, description: "Filter by end time" },
          { name: "fieldId", type: "string", required: false, description: "Filter by field ID" },
        ],
        response: `{ "calibrations": [...] }`,
      },
      {
        method: "GET",
        path: "/api/internal/attachments/:attachmentId/calibrations/updates",
        description: "Cursor-based incremental sync for a specific device. Includes soft-deleted records.",
        params: [
          { name: "cursor", type: "number", required: false, description: "Revision cursor (default 0)" },
          { name: "fieldId", type: "string", required: false, description: "Filter by field ID" },
          { name: "limit", type: "number", required: false, description: "Max records (default 100)" },
        ],
        response: `{ "calibrations": [...], "cursor": 42 }`,
      },
    ],
  },
  {
    title: "Admin - Attachments",
    description: "Device-user binding history.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/attachments",
        description: "List all device attachments",
        params: [
          { name: "esn", type: "string", required: false, description: "Filter by device ESN" },
          { name: "userId", type: "string", required: false, description: "Filter by user ID" },
          { name: "activeOnly", type: "boolean", required: false, description: "Only active (not detached) attachments" },
          { name: "limit", type: "number", required: false, description: "Page size (default 100)" },
          { name: "offset", type: "number", required: false, description: "Pagination offset" },
        ],
        response: `{ "attachments": [{ "id": "...", "userId": "...", "deviceEsn": "GG-001", "name": "Combine #3", "reaperWidth": 6.7, "createdAt": "...", "detachedAt": null }], "total": 150 }`,
      },
    ],
  },
  {
    title: "Admin - Seasons",
    description: "All seasons across all users.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/seasons",
        description: "List all seasons",
        params: [
          { name: "userId", type: "string", required: false, description: "Filter by user ID" },
          { name: "limit", type: "number", required: false, description: "Page size (default 100)" },
          { name: "offset", type: "number", required: false, description: "Pagination offset" },
        ],
        response: `{ "seasons": [{ "id": "...", "userId": "...", "name": "2025 Harvest", "start": "...", "end": "...", "isCurrent": true }], "total": 50 }`,
      },
    ],
  },
  {
    title: "Admin - MQTT Messages",
    description: "Raw MQTT message inspection for debugging device communication.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/messages",
        description: "List raw MQTT messages",
        params: [
          { name: "deviceEsn", type: "string", required: false, description: "Filter by device ESN" },
          { name: "messageType", type: "number", required: false, description: "Filter by type (0=INVALID, 1=REQUEST, 2=REQUEST_FEEDBACK, 3=CONNECTION_STATE, 4=ALERT)" },
          { name: "from", type: "datetime", required: false, description: "Start time" },
          { name: "to", type: "datetime", required: false, description: "End time" },
          { name: "limit", type: "number", required: false, description: "Page size (default 50)" },
          { name: "offset", type: "number", required: false, description: "Pagination offset" },
        ],
        response: `{ "messages": [{ "id": 1, "deviceEsn": "GG-001", "messageType": 4, "sensorValuesCount": 12, "parsed": true, "receivedAt": "..." }], "total": 10000 }`,
      },
      {
        method: "GET",
        path: "/api/admin/messages/:id",
        description: "Get message detail with parsed JSON and raw payload",
        response: `{ "id": 1, "topic": "+/alerts/+", "deviceEsn": "GG-001", "parsed": { ... }, "parseError": null, "payload": "base64..." }`,
      },
    ],
  },
  {
    title: "Admin - Settings Schema",
    description: "Global user settings schema management.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/settings/schema",
        description: "Get current settings schema",
        response: `{ "id": 1, "version": "1.0.0", "schema": { ... }, "createdAt": "...", "updatedAt": "..." }`,
      },
      {
        method: "PUT",
        path: "/api/admin/settings/schema",
        description: "Create or update settings schema",
        bodyParams: [
          { name: "version", type: "string", required: true, description: "Schema version (e.g. 1.0.0)" },
          { name: "schema", type: "object", required: true, description: "JSON schema object" },
        ],
        response: `{ "ok": true }`,
      },
    ],
  },
  {
    title: "User API - Devices",
    description: "User-scoped device endpoints. Requires authentication. Devices accessed via attachment ID.",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/devices",
        description: "List user's attached devices with positions",
        response: `{ "devices": [{ "id": "att-id", "esn": "GG-001", "name": "...", "lastSeenAt": "...", "lastPosition": [37.6, 55.7], "settings": { ... } }], "bbox": [minLon, minLat, maxLon, maxLat] }`,
      },
      {
        method: "GET",
        path: "/api/v1/devices/:id",
        description: "Get device details by attachment ID",
      },
      {
        method: "PATCH",
        path: "/api/v1/devices/:id",
        description: "Update device settings",
        bodyParams: [
          { name: "name", type: "string", required: false, description: "Device name" },
          { name: "deviceType", type: "number", required: false, description: "0-3 device type" },
          { name: "sensorMode", type: "number", required: false, description: "0-4 sensor mode" },
          { name: "width", type: "number", required: false, description: "Reaper width (meters)" },
          { name: "delay", type: "number", required: false, description: "Sensor delay (ms)" },
        ],
        response: `{ "ok": true }`,
      },
      {
        method: "GET",
        path: "/api/v1/devices/:id/tracks",
        description: "Get track data for device",
        params: [
          { name: "from", type: "datetime", required: true, description: "Start time (ISO 8601)" },
          { name: "to", type: "datetime", required: true, description: "End time (ISO 8601)" },
          { name: "limit", type: "number", required: false, description: "Max points (default 10000)" },
        ],
      },
      {
        method: "GET",
        path: "/api/v1/devices/:id/tracks/geometry",
        description: "Get track as GeoJSON LineString",
        params: [
          { name: "from", type: "datetime", required: true, description: "Start time (ISO 8601)" },
          { name: "to", type: "datetime", required: true, description: "End time (ISO 8601)" },
        ],
      },
      {
        method: "GET",
        path: "/api/v1/devices/:id/calibrations",
        description: "List device calibrations (paginated, filterable)",
        params: [
          { name: "page", type: "number", required: false, description: "Page number (default 1)" },
          { name: "perPage", type: "number", required: false, description: "Items per page (default 50, max 100)" },
          { name: "fieldId", type: "string", required: false, description: "Filter by field/crop ID" },
          { name: "from", type: "datetime", required: false, description: "Filter calibrations starting from this time" },
          { name: "to", type: "datetime", required: false, description: "Filter calibrations ending before this time" },
        ],
        response: `{ "calibrations": [...], "total": 10, "page": 1, "perPage": 50 }`,
      },
      {
        method: "POST",
        path: "/api/v1/devices/:id/calibrations",
        description: "Create a calibration",
        bodyParams: [
          { name: "fieldId", type: "string", required: true, description: "Field ID" },
          { name: "start", type: "datetime", required: true, description: "Start time (must be before stop, not in future)" },
          { name: "stop", type: "datetime", required: true, description: "Stop time" },
          { name: "weightKilograms", type: "number", required: true, description: "Weight in kg" },
          { name: "deviceTimezoneSeconds", type: "number", required: false, description: "Device timezone offset in seconds from UTC" },
        ],
      },
      {
        method: "GET",
        path: "/api/v1/devices/:id/requests",
        description: "List device commands/requests",
      },
    ],
  },
  {
    title: "User API - Fields",
    description: "User-scoped field management.",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/fields",
        description: "List user's fields",
      },
      {
        method: "POST",
        path: "/api/v1/fields",
        description: "Create a field",
        bodyParams: [
          { name: "name", type: "string", required: true, description: "Field name" },
          { name: "geometry", type: "GeoJSON", required: false, description: "Polygon or MultiPolygon" },
          { name: "areaHectares", type: "number", required: false, description: "Area in hectares" },
        ],
      },
      {
        method: "GET",
        path: "/api/v1/fields/:id",
        description: "Get field detail with cultures",
      },
      {
        method: "PATCH",
        path: "/api/v1/fields/:id",
        description: "Update field",
      },
      {
        method: "DELETE",
        path: "/api/v1/fields/:id",
        description: "Soft delete field",
      },
    ],
  },
  {
    title: "User API - Seasons & Crops",
    description: "Season and crop management.",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/seasons",
        description: "List user's seasons",
      },
      {
        method: "POST",
        path: "/api/v1/seasons",
        description: "Create a season",
      },
      {
        method: "GET",
        path: "/api/v1/crops",
        description: "List all crops",
      },
      {
        method: "POST",
        path: "/api/v1/crops",
        description: "Create a crop",
      },
    ],
  },
];

const EXTERNAL_API_SECTIONS: SectionDef[] = [
  {
    title: "Authentication",
    description: "All external API requests require an API key. Include it in the Authorization header as a Bearer token. API keys are created in the Admin Panel > API Keys tab.",
    endpoints: [
      {
        method: "GET",
        path: "Authorization: Bearer sk_live_...",
        description: "Include this header in every request",
      },
    ],
  },
  {
    title: "Yield Data",
    description: "Upload and manage yield data for fields. The primary use case for external integrations.",
    endpoints: [
      {
        method: "POST",
        path: "/api/external/fields/:id/yield/upload",
        description: "Upload yield CSV for a field and season",
        params: [
          { name: "seasonId", type: "string", required: true, description: "The season ID to upload yield data for" },
        ],
        bodyParams: [
          { name: "file", type: "File (CSV)", required: true, description: "CSV file with columns: hex, yield, mass-kg (optional). Content-Type: multipart/form-data" },
        ],
        response: `{
  "ok": true,
  "imported": 150,
  "skipped": 2,
  "parseErrors": ["Row 5: invalid yield value"],
  "aggregate": {
    "min": 30.2,
    "max": 68.1,
    "avg": 45.65,
    "totalMassKg": 24250.0,
    "count": 150
  }
}`,
      },
    ],
  },
  {
    title: "Devices",
    description: "List and query devices. Useful for discovering device ESNs and checking connectivity status.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/devices",
        description: "List all devices with hardware info and connectivity status",
        params: [
          { name: "search", type: "string", required: false, description: "Search by ESN, alias, IMEI, ICCID, or model" },
          { name: "limit", type: "number", required: false, description: "Page size (default 50)" },
          { name: "offset", type: "number", required: false, description: "Pagination offset (default 0)" },
        ],
        response: `{
  "devices": [{
    "esn": "3230343733...",
    "imei": "864011...",
    "iccid": "897010...",
    "alias": "Combine #1",
    "model": "GG-PRO",
    "lastConnectedAt": "2025-07-15T10:00:00Z",
    "attachedUser": { "id": "...", "name": "John Doe" }
  }],
  "total": 120
}`,
      },
      {
        method: "GET",
        path: "/api/admin/devices/:esn",
        description: "Get full device detail with attachment history",
        response: `{
  "device": { "esn": "...", "alias": "...", "model": "...", ... },
  "status": { "lastConnectedAt": "...", "lastLongitude": 37.6, "lastLatitude": 55.7 },
  "attachments": [{ "id": "...", "userId": "...", "name": "Combine #1" }]
}`,
      },
    ],
  },
  {
    title: "Users",
    description: "List and query users. Useful for resolving user IDs for field and yield operations.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/users",
        description: "List all users with device count",
        params: [
          { name: "search", type: "string", required: false, description: "Search by name, email, or organization" },
          { name: "limit", type: "number", required: false, description: "Page size (default 50)" },
          { name: "offset", type: "number", required: false, description: "Pagination offset (default 0)" },
        ],
        response: `{
  "users": [{
    "id": "...",
    "name": "John Doe",
    "email": "john@example.com",
    "organizationName": "Farm LLC",
    "deviceCount": 3
  }],
  "total": 337
}`,
      },
    ],
  },
  {
    title: "Fields",
    description: "List and query fields across all users. Required to get field IDs for yield upload.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/fields",
        description: "List all fields across all users",
        params: [
          { name: "userId", type: "string", required: false, description: "Filter by user ID" },
          { name: "search", type: "string", required: false, description: "Search by field name" },
          { name: "limit", type: "number", required: false, description: "Page size (default 100)" },
          { name: "offset", type: "number", required: false, description: "Pagination offset" },
        ],
        response: `{
  "fields": [{
    "id": "...",
    "userId": "...",
    "userName": "John Doe",
    "name": "North Field",
    "areaHectares": 42.5
  }],
  "total": 200
}`,
      },
    ],
  },
  {
    title: "Tracks",
    description: "Access raw GPS and sensor data from devices. Useful for analytics and data processing pipelines.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/tracks",
        description:
          "Get track points by device ESN. Two ways to slice it, and the difference matters: `from`/`to` filter DEVICE time (when the machine recorded the point), which a device can write into the past when it flushes points buffered while offline — a caller advancing a device-time window never sees those. `sinceId` and `createdFrom`/`createdTo` filter INGESTION time (when the row was written here), which only moves forward. Fetch by ingestion, file each point under its own `time`. Page by repeating with sinceId=<previous maxId> while hasMore is true.",
        params: [
          { name: "esn", type: "string", required: true, description: "Device ESN" },
          { name: "from", type: "datetime", required: false, description: "Device-time start (ISO 8601). Must be paired with `to`." },
          { name: "to", type: "datetime", required: false, description: "Device-time end (ISO 8601). Must be paired with `from`." },
          { name: "sinceId", type: "number", required: false, description: "Ingestion cursor: only rows with id greater than this, ordered by id ascending" },
          { name: "createdFrom", type: "datetime", required: false, description: "Ingestion-time start (ISO 8601)" },
          { name: "createdTo", type: "datetime", required: false, description: "Ingestion-time end (ISO 8601)" },
          { name: "limit", type: "number", required: false, description: "Max points (default 100000)" },
          { name: "sample", type: "number", required: false, description: "Return every Nth point (e.g. 5 = every 5th)" },
          { name: "includeTotal", type: "boolean", required: false, description: "Add `total` — rows matching the filter ignoring limit" },
        ],
        response: `{
  "esn": "GG-001",
  "length": 5432,
  "total": 5432,
  "orderedBy": "id",
  "maxId": 17190000,
  "hasMore": false,
  "tracks": [{
    "id": 1,
    "time": "2025-07-15T10:00:00Z",
    "createdAt": "2025-07-15T10:00:04Z",
    "longitude": 37.6,
    "latitude": 55.7,
    "value": 120.5,
    "freq": 850,
    "speed": 5.2
  }]
}`,
      },
      {
        method: "GET",
        path: "/api/admin/tracks/csv",
        description: "Export tracks as downloadable CSV file",
        params: [
          { name: "esn", type: "string", required: true, description: "Device ESN" },
          { name: "from", type: "datetime", required: true, description: "Start time (ISO 8601)" },
          { name: "to", type: "datetime", required: true, description: "End time (ISO 8601)" },
          { name: "sample", type: "number", required: false, description: "Return every Nth point" },
        ],
        response: `id,time,longitude,latitude,value,freq,course,...
1,2025-07-15T10:00:00Z,37.6,55.7,120.5,850,180,...`,
      },
      {
        method: "GET",
        path: "/api/admin/tracks/geometry",
        description: "Get track as GeoJSON FeatureCollection",
        params: [
          { name: "esn", type: "string", required: true, description: "Device ESN" },
          { name: "from", type: "datetime", required: true, description: "Start time (ISO 8601)" },
          { name: "to", type: "datetime", required: true, description: "End time (ISO 8601)" },
          { name: "sample", type: "number", required: false, description: "Return every Nth point" },
        ],
        response: `{
  "type": "FeatureCollection",
  "features": [{
    "type": "Feature",
    "geometry": { "type": "LineString", "coordinates": [[37.6, 55.7], ...] },
    "properties": { "esn": "GG-001", "values": [...], "speeds": [...] }
  }]
}`,
      },
    ],
  },
  {
    title: "Calibrations",
    description: "Read and update calibration data for yield coefficient computation.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/calibrations",
        description: "List calibrations with filtering",
        params: [
          { name: "esn", type: "string", required: false, description: "Filter by device ESN" },
          { name: "fieldId", type: "string", required: false, description: "Filter by field ID" },
          { name: "from", type: "datetime", required: false, description: "Filter by start time" },
          { name: "to", type: "datetime", required: false, description: "Filter by stop time" },
          { name: "needsCoefficient", type: "boolean", required: false, description: "Only calibrations without coefficient" },
          { name: "limit", type: "number", required: false, description: "Page size (default 100)" },
          { name: "offset", type: "number", required: false, description: "Pagination offset" },
        ],
        response: `{
  "calibrations": [{
    "id": "...",
    "attachmentId": "...",
    "fieldId": "...",
    "start": "2025-07-15T08:00:00Z",
    "stop": "2025-07-15T12:00:00Z",
    "weightKilograms": 5000,
    "coefficient": 0.85
  }],
  "total": 50
}`,
      },
      {
        method: "PUT",
        path: "/api/admin/calibrations/:id/coefficient",
        description: "Update calibration coefficient",
        bodyParams: [
          { name: "coefficient", type: "number", required: true, description: "Computed yield coefficient" },
        ],
        response: `{ "ok": true }`,
      },
      {
        method: "GET",
        path: "/api/admin/calibrations/updates",
        description: "Cursor-based incremental sync for calibration changes",
        params: [
          { name: "cursor", type: "number", required: false, description: "Revision number to start from (default 0)" },
          { name: "esn", type: "string", required: false, description: "Filter by device ESN" },
          { name: "fieldId", type: "string", required: false, description: "Filter by field ID" },
          { name: "limit", type: "number", required: false, description: "Max records (default 100, max 1000)" },
        ],
        response: `{
  "calibrations": [{
    "id": "...",
    "revision": 42,
    "coefficient": 0.85,
    "deletedAt": null
  }],
  "cursor": 42
}`,
      },
    ],
  },
  {
    title: "Seasons",
    description: "List seasons. Required to get season IDs for yield upload.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/seasons",
        description: "List all seasons",
        params: [
          { name: "userId", type: "string", required: false, description: "Filter by user ID" },
          { name: "limit", type: "number", required: false, description: "Page size (default 100)" },
          { name: "offset", type: "number", required: false, description: "Pagination offset" },
        ],
        response: `{
  "seasons": [{
    "id": "...",
    "userId": "...",
    "name": "2025 Harvest",
    "start": "2025-06-01",
    "end": "2025-10-31",
    "isCurrent": true
  }],
  "total": 50
}`,
      },
    ],
  },
];

const DOC_TABS = [{ name: "External API" }, { name: "Internal API" }];

const EndpointItem: React.FC<{ endpoint: EndpointDef }> = ({ endpoint }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Endpoint expanded={expanded}>
      <EndpointHeader onClick={() => setExpanded(!expanded)}>
        <MethodBadge method={endpoint.method}>{endpoint.method}</MethodBadge>
        <EndpointPath>{endpoint.path}</EndpointPath>
        <EndpointDesc>{endpoint.description}</EndpointDesc>
      </EndpointHeader>
      {expanded && (
        <EndpointDetail>
          {endpoint.params && endpoint.params.length > 0 && (
            <>
              <Label>Query Parameters</Label>
              <ParamsTable>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Required</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoint.params.map((p) => (
                    <tr key={p.name}>
                      <td>
                        <code>{p.name}</code>
                      </td>
                      <td>{p.type}</td>
                      <td>{p.required ? "Yes" : "No"}</td>
                      <td>{p.description}</td>
                    </tr>
                  ))}
                </tbody>
              </ParamsTable>
            </>
          )}
          {endpoint.bodyParams && endpoint.bodyParams.length > 0 && (
            <>
              <Label>Request Body (JSON)</Label>
              <ParamsTable>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Required</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoint.bodyParams.map((p) => (
                    <tr key={p.name}>
                      <td>
                        <code>{p.name}</code>
                      </td>
                      <td>{p.type}</td>
                      <td>{p.required ? "Yes" : "No"}</td>
                      <td>{p.description}</td>
                    </tr>
                  ))}
                </tbody>
              </ParamsTable>
            </>
          )}
          {endpoint.response && (
            <>
              <Label>Response Example</Label>
              <ResponseExample>{endpoint.response}</ResponseExample>
            </>
          )}
        </EndpointDetail>
      )}
    </Endpoint>
  );
};

const SectionList: React.FC<{ sections: SectionDef[]; defaultOpen?: number[] }> = ({
  sections,
  defaultOpen = [0, 1, 2, 3],
}) => {
  const [openSections, setOpenSections] = useState<Set<number>>(
    new Set(defaultOpen)
  );

  const toggleSection = (index: number) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <>
      {sections.map((section, sIndex) => (
        <Section key={sIndex}>
          <SectionHeader
            open={openSections.has(sIndex)}
            onClick={() => toggleSection(sIndex)}
          >
            <Arrow open={openSections.has(sIndex)}>&#9654;</Arrow>
            <Text size={15} type="heading" color="#181B1F">
              {section.title}
            </Text>
            <Text size={12} color="#899093" style={{ marginLeft: 8 }}>
              {section.endpoints.length} endpoint
              {section.endpoints.length !== 1 ? "s" : ""}
            </Text>
          </SectionHeader>
          {openSections.has(sIndex) && (
            <EndpointList>
              <Text
                size={12}
                color="#899093"
                style={{ marginBottom: 8, display: "block" }}
              >
                {section.description}
              </Text>
              {section.endpoints.map((endpoint, eIndex) => (
                <EndpointItem key={eIndex} endpoint={endpoint} />
              ))}
            </EndpointList>
          )}
        </Section>
      ))}
    </>
  );
};

const AdminApiDocsTab: React.FC = () => {
  const [docTab, setDocTab] = useState(0);

  return (
    <Container>
      <Tabs
        tabs={DOC_TABS}
        activeTab={docTab}
        setActive={setDocTab}
        style={{ marginBottom: 24 }}
      />

      {docTab === 0 && (
        <>
          <Note>
            These endpoints are designed for external services and scripts using API key authentication.
            Include the API key in the <code>Authorization</code> header:{" "}
            <code>Bearer sk_live_...</code>
            <br />
            Base URL: <code>{process.env.REACT_APP_API_URL ?? window.location.origin}</code>
          </Note>

          <Text size={13} color="#899093" style={{ marginBottom: 24, display: "block" }}>
            Share this documentation with external services that integrate with your platform.
            Click on any endpoint to see parameters and response examples.
          </Text>

          <SectionList sections={EXTERNAL_API_SECTIONS} defaultOpen={[0, 1, 2]} />
        </>
      )}

      {docTab === 1 && (
        <>
          <Note>
            All admin endpoints require authentication via session cookies and
            the user's email must be in the <code>ADMIN_EMAILS</code> environment
            variable. Base URL: <code>{process.env.REACT_APP_API_URL ?? window.location.origin}</code>
          </Note>

          <Text size={13} color="#899093" style={{ marginBottom: 24, display: "block" }}>
            Full internal API reference for debugging and administration.
            Click on any endpoint to see parameters and response examples.
          </Text>

          <SectionList sections={API_SECTIONS} />
        </>
      )}
    </Container>
  );
};

export default AdminApiDocsTab;
